import { DecimalPipe } from '@angular/common';
import { Injectable, PipeTransform } from '@angular/core';
import { SortDirection } from '@modules/tables/directives';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { debounceTime, delay, switchMap, tap } from 'rxjs/operators';

interface SearchResult {
    mytables: Record<string, any>[];
    total: number;
}

type SearchOperator = 'contains' | 'startswith' | 'endswith';

interface State {
    page: number;
    pageSize: number;
    searchTerm: string;
    searchOperator: SearchOperator;
    sortColumn: string;
    sortDirection: SortDirection;
}

function compare(v1: number | string, v2: number | string) {
    return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
}

/**
 * Dynamic sorting function
 */
function sort(
    data: Record<string, any>[],
    column: string,
    direction: string
): Record<string, any>[] {
    if (!column || direction === '') return data;

    return [...data].sort((a, b) => {
        const valueA = a[column] ?? ''; // 2. Handle missing keys gracefully
        const valueB = b[column] ?? '';
        const res = compare(valueA, valueB);
        return direction === 'asc' ? res : -res;
    });
}

/**
 * Dynamic filtering function
 */
function matches(
    item: Record<string, any>,
    term: string,
    operator: SearchOperator,
    pipe: PipeTransform
): boolean {
    if (!term) return true;
    const normalizedTerm = term.toLowerCase();

    // 3. Filter across all keys dynamically
    return Object.values(item).some((value) => {
        const candidate = value?.toString().toLowerCase() ?? '';
        switch (operator) {
            case 'startswith':
                return candidate.startsWith(normalizedTerm);
            case 'endswith':
                return candidate.endsWith(normalizedTerm);
            default:
                return candidate.includes(normalizedTerm);
        }
    });
}

@Injectable()
export class CustomTableService {
    private _loading$ = new BehaviorSubject<boolean>(true);
    private _search$ = new Subject<void>();
    private _currentPageRows$ = new BehaviorSubject<Record<string, any>[]>([]); // 1. Use dynamic type
    private _total$ = new BehaviorSubject<number>(0);
    private _tableRowItemsRaw$ = new BehaviorSubject<Record<string, any>[]>([]); // 1. Use dynamic type
    private _serverSidePagination = false;
    private _externalTotalItems: number | null = null;

    private _state: State = {
        page: 1,
        pageSize: 8,
        searchTerm: '',
        searchOperator: 'contains',
        sortColumn: '',
        sortDirection: '',
    };

    constructor(private pipe: DecimalPipe) {
        this._search$
            .pipe(
                tap(():any => {
                    // console.log('ctor-Search triggered, setting loading to true.');
                    this._loading$.next(true);
                }),
                debounceTime(120),
                switchMap(() => {
                    // console.log('ctor-After debounce, executing _search method.');
                    return this._search();
                }),
                delay(120),
                tap(() => {
                    // console.log('ctor-Setting loading to false.');
                    this._loading$.next(false);
                })
            )
            .subscribe((result) => {
                // console.log('ctor-Received search result:', result);
                this._currentPageRows$.next(result.mytables);
                this._total$.next(result.total);
            });

        // console.log('ctor-Initial search triggered.');
        this._search$.next();
    }
    get tableRowItemsRaw$(): Observable<Record<string, any>[]> {
        return this._tableRowItemsRaw$.asObservable();
    }

    set tableRowItemsRaw(data: Record<string, any>[]) {
        this._tableRowItemsRaw$.next(data);
    }

    get currentPageRows$() {
        return this._currentPageRows$.asObservable();
    }
    get total$() {
        return this._total$.asObservable();
    }
    get loading$() {
        return this._loading$.asObservable();
    }
    get page() {
        return this._state.page;
    }
    set page(page: number) {
        this._set({ page });
    }
    get pageSize() {
        return this._state.pageSize;
    }
    set pageSize(pageSize: number) {
        this._set({ pageSize });
    }
    get searchTerm() {
        return this._state.searchTerm;
    }
    set searchTerm(searchTerm: string) {
        this._set({ searchTerm });
    }
    /**
     * Updates search term and page together so search-clear flows can restore
     * the previous page without rendering an intermediate page-1 state.
     */
    applySearchState(searchTerm: string, page?: number) {
        const patch: Partial<State> = { searchTerm };
        if (typeof page === 'number' && Number.isFinite(page) && page > 0) {
            patch.page = Math.floor(page);
        }
        this._set(patch);
    }
    get searchOperator() {
        return this._state.searchOperator;
    }
    set searchOperator(searchOperator: SearchOperator) {
        this._set({ searchOperator });
    }
    set sortColumn(sortColumn: string) {
        this._set({ sortColumn });
    }
    set sortDirection(sortDirection: SortDirection) {
        this._set({ sortDirection });
    }

    set serverSidePagination(value: boolean) {
        this._serverSidePagination = value;
        this._search$.next();
    }

    set externalTotalItems(value: number | null) {
        this._externalTotalItems = value;
        this._search$.next();
    }

    private _set(patch: Partial<State>) {
        Object.assign(this._state, patch);
        this._search$.next();
    }

    updateTableData(data: Record<string, any>[]) {
        //// Retain the current page instead of resetting it
        // const currentPage = this._state.page;
        //
        // this._currentPageRows$.next(data);
        // this._set({ page: currentPage }); // Keep the page state unchanged

        this._tableRowItemsRaw$.next(data);
        this._search$.next();
    }

    private _search(): Observable<SearchResult> {
        const { sortColumn, sortDirection, pageSize, page, searchTerm, searchOperator } =
            this._state;

        let tableItems = sort(this._tableRowItemsRaw$.getValue(), sortColumn, sortDirection);
        tableItems = tableItems.filter((item) =>
            matches(item, searchTerm, searchOperator, this.pipe)
        );
        if (this._serverSidePagination) {
            const total =
                this._externalTotalItems !== null
                    ? this._externalTotalItems
                    : tableItems.length;
            return of({ mytables: tableItems, total });
        }

        // Paginate results
        const total = tableItems.length;
        tableItems = tableItems.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);

        return of({ mytables: tableItems, total });
    }
}
